import { Platform } from '@angular/cdk/platform'
import { DataSource } from '@angular/cdk/table'
import { AfterViewInit, Component, ElementRef, ErrorHandler, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy, Injector, inject, signal } from '@angular/core'
import { FieldTree, form } from '@angular/forms/signals'
import { DateAdapter } from '@angular/material/core'
import { MatDatepicker } from '@angular/material/datepicker'
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog'
import { MatPaginator } from '@angular/material/paginator'
import { MatSort } from '@angular/material/sort'
import { MatTableDataSource } from '@angular/material/table'
import { Router } from '@angular/router'

import { LEMONATOR_CF_API, LemonHttpService } from 'app/_angular/service/lemon-http.service'
import { LaskuKommenttiDialog } from './dialogit/lasku-kommentti-lemonator.dialog'
import { LaskuLataaDialog, LaskuLataaDialogData } from './dialogit/lasku.lataa.dialog'

import { LoadingConfig } from 'app/_jaettu-angular/_components/loading.component'
import { LaskuKatseleComponentDataResolve, LaskuKatseleComponentExistingData } from '../_angular/_resolvers/lasku.resolve'
import { AsiakasService } from '../_angular/service/asiakas/asiakas.service'
import { LaskuService } from '../_angular/service/lasku/lasku.service'

import { LemonKuukausiDateAdapter } from '../_jaettu-angular/_material/LemonKuukausiDateAdapter'
import { LemonTranslationService } from '../_jaettu-angular/service/lemon-translation.service'
import { WindowSizeService } from '../_jaettu-angular/service/window.service'

import { Lasku, LaskuBase, LaskunAsiakas, LaskunListaustietorivi, LaskunTila } from '@lemon/core-firebase/model/lasku'
import { DateService } from '@lemon/core/service/date.service'
import { KirjanpitajaService } from '../_angular/service/kirjanpitaja/kirjanpitaja.service'
import { Kirjanpitaja } from '../_jaettu-lemonator/model/kirjanpitaja'

import { LaskunLokalisoituTila } from './laskut.firestore.datasource'
import { LaskutUusiDataSourceService } from './laskut.uusi.datasource.service'

import { firstValueFrom, Subject } from 'rxjs'
import { debounceTime, take, takeUntil } from 'rxjs/operators'

import { arvonMuutokset, asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { FileSaverService } from 'app/_jaettu-angular/service/file-saver'

export type LaskuProperties = 'nro' | 'pvm' | 'erapvm' | 'summa' | 'avoinna' | 'tila' | 'lataa' | 'kommentti' | undefined

export interface LaskuKommenttiDialogData {
  lasku: Lasku
  kirjanpitaja: Kirjanpitaja
}

interface LaskutLomake {
  nimiFirestore: string
  vuosikk: Date | null
}

export interface SummienRullausData {
  juurilasku: Lasku
  kasiteltava: LaskuBase
  edellinenLaskunTila: LaskunTila
}

@Component({
  selector: 'app-laskut',
  templateUrl: './laskut.component.html',
  styleUrls: ['./laskut.component.css'],
  providers: [
    // `MomentDateAdapter` can be automatically provided by importing `MomentDateModule` in your
    // application's root module. We provide it at the component level here, due to limitations of
    // our example generation script.
    { provide: DateAdapter, useClass: LemonKuukausiDateAdapter, deps: [ErrorHandler, DateService, Platform, LemonTranslationService] },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LaskutComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('laskutaulu', { static: true }) laskutaulu: ElementRef
  @ViewChild(MatPaginator) _paginator: MatPaginator
  @ViewChild(MatSort, { static: true }) _sort: MatSort

  private ngUnsubscribe = new Subject<void>()
  private readonly _injector = inject(Injector)
  private _kommenttiDialogi: MatDialogRef<LaskuKommenttiDialog> = null

  lomake: FieldTree<LaskutLomake>

  readonly propertiesToDisplay = signal<LaskuProperties[]>([])
  tilat: LaskunLokalisoituTila[] = []

  naytaHuomautus = false
  naytaKaikkiSummat = true

  loadingSettings: LoadingConfig = {
    backdropBackgroundColour: 'rgba(0,0,0,0.15)',
    backdropBorderRadius: '4px',
    fullScreenBackdrop: false,
    primaryColour: 'rgba(255, 255, 255, 1)',
    secondaryColour: 'rgba(255, 255, 255, 1)', // rgba(252, 238, 32, .15)
    tertiaryColour: 'rgba(255, 255, 255, 1)',
  }

  constructor(
    private router: Router,
    private errorHandler: ErrorHandler,
    private dialog: MatDialog,
    private http: LemonHttpService,
    private lemonTranslation: LemonTranslationService,
    private laskuService: LaskuService,
    private windowSizeService: WindowSizeService,
    private translationService: LemonTranslationService,
    private dateAdapter: DateAdapter<Date>,
    private laskuKatseleComponentDataResolve: LaskuKatseleComponentDataResolve,
    private asiakasService: AsiakasService,
    private laskutDataSourceService: LaskutUusiDataSourceService,
    private _fileSaverService: FileSaverService,
    private _kirjanpitajaService: KirjanpitajaService,
  ) {}

  ngOnInit() {
    this._sort.sort({
      disableClear: true,
      id: 'nro',
      start: 'desc',
    })

    this.asetaListauksenKolumnit()

    this.translationService.currentLanguageObservable.pipe(takeUntil(this.ngUnsubscribe)).subscribe(kieli => {
      this.dateAdapter.setLocale(kieli)
    })

    // this.laskuService.asetuksetObservable.pipe(
    //   takeUntil(this.ngUnsubscribe)
    // ).subscribe(asetukset => {
    //   if (asetukset && !asetukset.summahuomautusPiilotettu) {
    //     this.naytaHuomautus = true
    //   } else {
    //     this.naytaHuomautus = false
    //   }
    // }, error => {
    //   this.errorHandler.handleError(error)
    // })

    // this.laskuSummatService.summatObservable.pipe(
    //   takeUntil(this.ngUnsubscribe)
    // ).subscribe(summat => {
    //   this.summat = summat
    //   if (summat && summat.length > 0) {
    //     const summa = summat[0]
    //     this.suurinSumma = summa
    //   }
    //   // if (summat && summat.length > 1) {
    //   //   this.naytaKaikkiSummat = true
    //   // } else {
    //   //   this.naytaKaikkiSummat = false
    //   // }
    // }, error => {
    //   this.errorHandler.handleError(error)
    // })

    this.laskutDataSourceService.laskunTilaObservable.pipe(takeUntil(this.ngUnsubscribe)).subscribe(
      tilat => {
        this.tilat = tilat
      },
      error => {
        this.errorHandler.handleError(error)
      },
    )

    // const vuosiKk = this.dataSource.getVuosiKk()
    // const date = vuosiKk && vuosiKk.vuosi && vuosiKk.kk ? new Date(vuosiKk.vuosi, vuosiKk.kk, 1) : null
    this.lomake = form(
      signal<LaskutLomake>({
        nimiFirestore: this.matDataSource.filter ?? '',
        vuosikk: null,
        // 'vuosikkkohde': new FormControl( vuosiKk.kohde, [] ),
        // 'tila': new FormControl( this.dataSource.getTila(), [] )
      }),
      { injector: this._injector },
    )

    // this.form.get('vuosikkkohde').valueChanges.subscribe((value: 'p' | 'e') => {
    //   this.dataSource.setVuosiKkKohde(value)
    // })
    // this.form.get('vuosikk').valueChanges.subscribe((value: Date) => {
    //   console.log('asetettu vuosikk; ', value)
    //   if (value) {
    //     this.dataSource.setVuosiKk(value.getFullYear(), value.getMonth())
    //   } else {
    //     this.dataSource.setVuosiKk(null, null)
    //   }
    // })
    // this.form.get('tila').valueChanges.subscribe((value: LaskunTila) => {
    //   this.dataSource.setTila(value)
    // })
    arvonMuutokset(this.lomake.nimiFirestore, { injector: this._injector })
      .pipe(debounceTime(500))
      .subscribe((value: string) => {
        if (value) {
          const val = value.trim().toLowerCase()
          this.matDataSource.filter = val
        } else {
          this.matDataSource.filter = null
        }
      })

    const currentSort = this.matDataSource.sort
    if (currentSort) {
      this._sort.sort({
        id: currentSort.active,
        disableClear: true,
        start: currentSort.direction === 'asc' ? 'asc' : 'desc',
      })
    }
    this.matDataSource.sort = this._sort

    // const currentSort = this.dataSource.getSort()
    // this.sort.sort({
    //   id: currentSort.active,
    //   disableClear: true,
    //   start: currentSort.direction === 'asc' ? 'asc' : 'desc'
    // })
    // this.sort.sortChange.subscribe(sort => {
    //   this.dataSource.changeSort(sort)
    // }, error => {
    //   this.errorHandler.handleError(error)
    // })

    // this._paginator.page.subscribe(page => {
    //   this.dataSource.changePage(page)
    // }, error => {
    //   this.errorHandler.handleError(error)
    // })

    // this.route.data.pipe(
    //   takeUntil(this.ngUnsubscribe)
    // ).subscribe(( data: { data: SummienRullausData } ) => {

    //   if (data && data.data && data.data.kasiteltava && data.data.juurilasku && data.data.edellinenLaskunTila) {

    //     // console.log('paukutetaan')

    //     // this.dataSource.resetSearchToDefaults()

    //     // setTimeout(() => {

    //       // console.log('rullataan')
    //       // this.laskuSummatService.lisaaLaskunSummat(data.data, false)

    //     // }, 500)

    //   }
    // }, error => {
    //   this.errorHandler.handleError(error)
    // })
  }

  chosenMonthHandler(normalizedMonth: Date, datepicker: MatDatepicker<Date>) {
    asetaArvo(this.lomake.vuosikk, normalizedMonth)
    datepicker.close()
  }

  ngAfterViewInit() {
    this.windowSizeService.sizeObservable.pipe(takeUntil(this.ngUnsubscribe)).subscribe(koko => {
      setTimeout(() => {
        this.asetaListauksenKolumnit()
      }, 250)
    })
  }

  private asetaListauksenKolumnit() {
    const leveys = this.laskutaulu.nativeElement.offsetWidth
    // if (tablenKoko < 500) {
    //   this.propertiesToDisplay = [                     'erapvm',         'avoinna'/*, 'merkitse'*/]
    // } else
    if (leveys < 600) {
      this.propertiesToDisplay.set(['tila', 'erapvm', 'avoinna', 'lataa'])
    } else if (leveys < 700) {
      this.propertiesToDisplay.set(['tila', 'erapvm', 'summa', 'avoinna', 'lataa'])
    } else if (leveys < 800) {
      this.propertiesToDisplay.set(['tila', 'pvm', 'erapvm', 'summa', 'avoinna', 'lataa', 'kommentti'])
    } else {
      this.propertiesToDisplay.set(['tila', 'nro', 'pvm', 'erapvm', 'summa', 'avoinna', 'lataa', 'kommentti'])
    }
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next()
    this.ngUnsubscribe.complete()
  }

  getDisplayFn() {
    return val => this.display(val)
  }

  private display(laskunAsiakas: LaskunAsiakas): string {
    if (laskunAsiakas && laskunAsiakas.nimi) {
      return laskunAsiakas.nimi
    }
    return ''
  }

  onCountoEndAvoinna() {
    // this.laskuSummatService.asetaSummatAlkuasentoon()
  }

  onCountoEndSaatu() {
    // this.laskuSummatService.asetaSummatAlkuasentoon()
  }

  onCountoEndEraantynyt() {
    // this.laskuSummatService.asetaSummatAlkuasentoon()
  }

  lataaPdf(rivi: LaskunListaustietorivi) {
    if ((rivi.kasiteltava.print && rivi.kasiteltava.print.done) || (rivi.kasiteltava.email && rivi.kasiteltava.email.done) || (rivi.kasiteltava.sahkoinen && rivi.kasiteltava.sahkoinen.done)) {
      const pdfUri = this.laskuService.getPdfUrl(rivi.kasiteltava)
      const url = '/api/1/laskut/lataaPdf/' + pdfUri
      return this.http
        .getBinary('/laskuLataaPdf?a=' + encodeURIComponent(url) + '&time=' + encodeURIComponent(new Date().getTime()), LEMONATOR_CF_API)
        .then(result => {
          const nimi = this.laskuService.annaPdfTiedostonNimi(rivi.juurilasku, rivi.kasiteltava, this.lemonTranslation.nykyinenKieli)
          this._fileSaverService.saveAs(result, nimi)
        })
        .catch(err => {
          this.errorHandler.handleError(err)
        })
    } else {
      const data: LaskuLataaDialogData = {
        juurilasku: rivi.juurilasku,
        kasiteltava: rivi.kasiteltava,
      }
      this.dialog.open(LaskuLataaDialog, { data: data, panelClass: 'ilman-paddingia' })
    }
  }

  muokkaa(rivi: LaskunListaustietorivi) {
    this.asiakasService.nykyinenAsiakasAvainObservable.pipe(take(1)).subscribe(asiakas => {
      if (asiakas) {
        const existing: LaskuKatseleComponentExistingData = {
          juurilasku: rivi.juurilasku,
          kasiteltava: rivi.kasiteltava,
        }
        this.laskuKatseleComponentDataResolve.asetaOlemassaolevaData(existing)
        this.router.navigate(['asiakkaat', asiakas.avain, 'laskutus', 'katsele', rivi.juuriAvain, rivi.avain])
      }
    })
  }

  // lisaa() {
  //   this.router.navigate(['/laskutus/laskut/', 'uusi', 'uusi']).then(result => {
  //     this.vasenValikkoService.paivitaAuki(false)
  //   })
  // }

  get dataSource(): DataSource<LaskunListaustietorivi> {
    return this.laskutDataSourceService.dataSourceTransformed
  }

  get matDataSource(): MatTableDataSource<Lasku> {
    return this.laskutDataSourceService
  }

  get lataa(): boolean {
    return this.laskutDataSourceService.lataa()
  }

  get naytettaviaLaskuja(): number {
    return this.laskutDataSourceService.naytettaviaLaskuja()
  }

  async merkitseMaksetuksi(rivi: LaskunListaustietorivi) {
    // if (rivi.avoinna > 0) {
    //   await this.laskuService.merkitseLaskuTaysinMaksetuksi(rivi)
    // } else {
    //   const data: LaskuHaluatkoVarmastiPeruaMaksetuksiMerkitsemisenDialogData = {
    //     rivi: rivi
    //   }
    //   const settings: MatDialogConfig = {
    //     data: data,
    //     panelClass: 'ilman-paddingia',
    //     maxWidth: '1600px'
    //     // ,
    //     // maxHeight: korkeus,
    //     // width: leveys
    //   }
    //   this.dialog.open(LaskuHaluatkoVarmastiPeruaMaksetuksiMerkitsemisenDialog, settings)
    // }
  }

  // avaaKaikkiSummat() {
  //   const data: LaskuSummatKaikkiDialogData = {
  //     summat: this.summat
  //   }
  //   this.dialog.open(LaskuSummatKaikkiDialog, { data: data })
  // }

  async modifyComment(lasku: Lasku) {
    if (this._kommenttiDialogi) {
      return
    }

    const kaytettavaLeveys = window.innerWidth
    const kaytettavaKorkeus = window.innerHeight
    const leveys = kaytettavaLeveys > 900 ? '90vw' : '98vw'
    const korkeus = kaytettavaKorkeus > 1100 ? '80vh' : kaytettavaKorkeus > 750 ? '85vh' : '92vh'
    const kirjanpitaja = await firstValueFrom(this._kirjanpitajaService.kirjautuneenKayttajanKirjanpitajaObservable)

    const settings: MatDialogConfig<LaskuKommenttiDialogData> = {
      maxWidth: '900px',
      maxHeight: korkeus,
      width: leveys,
      data: { lasku: lasku, kirjanpitaja: kirjanpitaja },
    }

    this._kommenttiDialogi = this.dialog.open(LaskuKommenttiDialog, settings)
    this._kommenttiDialogi.afterClosed().subscribe(result => {
      this._kommenttiDialogi = null
      if (result === 'onnistui') {
        /* NOOP */
      }
    })
  }
}
